import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import firebase from '../store/firebase';
import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils';

const headerStyle = {
  fontWeight: 'bold',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd'
};

const cellStyle = {
  padding: '8px',
  borderBottom: '1px solid #eee',
  fontSize: '12px'
};

const sectionTitle = {
  fontSize: '16px',
  fontWeight: 'bold',
  margin: '10px 0 6px'
};

function CompanyTestSummary({ userdata }) {
  const [loading, setLoading] = useState(true);
  const [allPassedTests, setAllPassedTests] = useState([]);
  const [companies, setCompanies] = useState([]); // [{ id, name }]
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false
  );

  // Mobile detection
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // ────────────────────────────────────────────────
  //  cleanMainCategory is defined HERE — before any useMemo that uses it
  // ────────────────────────────────────────────────
  const cleanMainCategory = (name) => {
    if (!name) return '—';
    const parts = String(name).split(/\s*[–—-]\s*/);
    return parts[0].trim();
  };

  // Load companies list
  useEffect(() => {
    if (!userdata) return;

    const companiesRef = firebase.database.ref('active-companies');
    companiesRef.once('value', (snap) => {
      const comps = [];
      snap.forEach((child) => {
        const val = child.val();
        if (val?.companyID && val?.companyName) {
          comps.push({
            id: val.companyID,
            name: val.companyName,
          });
        }
      });
      comps.sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(comps);

      // Pre-select current user's company if possible
      const userCompanyID = userdata.real_companyID || userdata.companyID;
      if (userCompanyID && comps.some(c => c.id === userCompanyID)) {
        setSelectedCompany(userCompanyID);
      }
    }).catch(err => {
      console.error('Error loading companies:', err);
    });
  }, [userdata]);

  // Load test results
  useEffect(() => {
    if (!userdata || companies.length === 0) return;

    setLoading(true);
    setAllPassedTests([]);

    const processTest = (test, userId = null, companyId = null) => {
      const total = parseInt(test.totalAnswered || test.qlen || 0);
      const score = parseInt(test.testscore || 0);
      const passedFlag =
        typeof test.testPassed === 'boolean'
          ? test.testPassed
          : calculateTestPassed(score, total, test.difficulty || 'basic');

      if (!passedFlag) return null;

      const lc =
        typeof test.lcEarned === 'number'
          ? test.lcEarned
          : passedFlag
            ? calculateLCEarned(score, true, test.difficulty || 'basic')
            : 0;

      return {
        surveyid: test.surveyid || '',
        userId,
        companyId,
        category: test.leadCategory || test.categoryType || 'general',
        subCategory: test.leadSubCategory || '',
        lc,
        difficulty: test.difficulty || 'basic',
      };
    };

    const fetchForCompany = async (companyID) => {
      const ref = firebase.database.ref(`userTestResults/${companyID}`);
      try {
        const snap = await ref.once('value');
        const companyData = snap.val() || {};
        const passed = [];

        Object.entries(companyData).forEach(([userId, userTests]) => {
          if (!userTests || typeof userTests !== 'object') return;
          Object.entries(userTests).forEach(([surveyid, test]) => {
            const processed = processTest({ ...test, surveyid }, userId, companyID);
            if (processed) passed.push(processed);
          });
        });
        return passed;
      } catch (err) {
        console.error(`Error reading ${companyID}:`, err);
        return [];
      }
    };

    const loadData = async () => {
      let passed = [];
      if (selectedCompany === 'all') {
        for (const comp of companies) {
          const companyPassed = await fetchForCompany(comp.id);
          passed = passed.concat(companyPassed);
        }
      } else {
        passed = await fetchForCompany(selectedCompany);
      }
      setAllPassedTests(passed);
      setLoading(false);
    };

    loadData();
  }, [userdata, selectedCompany, companies]);

  // Compute available main categories from current data
  const availableCategories = useMemo(() => {
    if (allPassedTests.length === 0) return [];
    const cats = new Set();
    allPassedTests.forEach(t => {
      const cat = cleanMainCategory(t.category);
      if (cat && cat !== '—') cats.add(cat);
    });
    return Array.from(cats).sort();
  }, [allPassedTests]);

  // Filtered totals
  const totals = useMemo(() => {
    let filtered = allPassedTests;

    if (selectedCategory !== 'all') {
      filtered = allPassedTests.filter(
        t => cleanMainCategory(t.category) === selectedCategory
      );
    }

    const byCategory = {};
    const bySubCategory = {};
    let totalLC = 0;
    let uniqueUsers = new Set();

    filtered.forEach(t => {
      const cat = cleanMainCategory(t.category);
      const sub = t.subCategory;
      const lc = Number(t.lc || 0);

      byCategory[cat] = (byCategory[cat] || 0) + lc;
      if (sub) bySubCategory[sub] = (bySubCategory[sub] || 0) + lc;
      totalLC += lc;
      if (t.userId) uniqueUsers.add(t.userId);
    });

    return {
      totalPassedTests: filtered.length,
      uniquePeoplePassed: uniqueUsers.size,
      totalLC,
      byCategory,
      bySubCategory,
    };
  }, [allPassedTests, selectedCategory]);

  if (!userdata) {
    return <div style={{ padding: 24 }}>Please sign in.</div>;
  }

  const companyName =
    selectedCompany === 'all'
      ? 'All Companies'
      : companies.find(c => c.id === selectedCompany)?.name || 'Selected Company';

  const categoryDisplay =
    selectedCategory === 'all' ? 'All Categories' : selectedCategory;

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ margin: '0 0 20px' }}>
        Test Results Overview – {companyName} – {categoryDisplay}
      </h2>

      {/* Company selector */}
      <div style={{ marginBottom: 20, maxWidth: 400 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>
          Company
        </label>
        <select
          value={selectedCompany}
          onChange={(e) => {
            setSelectedCompany(e.target.value);
            setSelectedCategory('all'); // reset category when company changes
          }}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '16px',
            borderRadius: '6px',
            border: '1px solid #ccc',
          }}
        >
          <option value="all">All Companies</option>
          {companies.map(comp => (
            <option key={comp.id} value={comp.id}>
              {comp.name}
            </option>
          ))}
        </select>
      </div>

      {/* Category selector */}
      <div style={{ marginBottom: 32, maxWidth: 400 }}>
        <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>
          Main Category
        </label>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          disabled={loading || availableCategories.length === 0}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '16px',
            borderRadius: '6px',
            border: '1px solid #ccc',
          }}
        >
          <option value="all">All Categories</option>
          {availableCategories.map(cat => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginBottom: 32,
            }}
          >
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>
                People with ≥1 passed test
              </div>
              <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.uniquePeoplePassed}</div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>
                Total Passed Tests
              </div>
              <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalPassedTests}</div>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>
                Total LC Earned
              </div>
              <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalLC.toFixed(1)} LC</div>
            </div>
          </div>

          {/* LC by Main Category - only show when not filtering by category */}
          {selectedCategory === 'all' && (
            <>
              <h3 style={{ margin: '32px 0 16px' }}>LC by Main Category</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 32 }}>
                {Object.keys(totals.byCategory).length === 0 ? (
                  <div style={{ color: '#94a3b8' }}>No passed tests recorded yet</div>
                ) : (
                  Object.entries(totals.byCategory)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cat, lc]) => (
                      <div
                        key={cat}
                        style={{
                          background: '#f1f5f9',
                          borderRadius: 999,
                          padding: '8px 16px',
                          fontSize: 14,
                        }}
                      >
                        <strong>{cleanMainCategory(cat)}</strong>: {lc.toFixed(1)} LC
                      </div>
                    ))
                )}
              </div>
            </>
          )}

          {selectedCategory !== 'all' && totals.totalLC > 0 && (
            <div style={{ margin: '20px 0 32px', fontSize: '18px' }}>
              <strong>{selectedCategory}</strong>: {totals.totalLC.toFixed(1)} LC earned
            </div>
          )}
        </>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  userdata: state.userDialog.userDetails[0] || null,
});

export default connect(mapStateToProps)(CompanyTestSummary);
////////////////////////////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useState } from 'react';
// import { connect } from 'react-redux';
// import firebase from '../store/firebase';
// import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils';

// const headerStyle = {
//   fontWeight: 'bold',
//   backgroundColor: '#f5f5f5',
//   borderBottom: '1px solid #ddd'
// };

// const cellStyle = {
//   padding: '8px',
//   borderBottom: '1px solid #eee',
//   fontSize: '12px'
// };

// const sectionTitle = {
//   fontSize: '16px',
//   fontWeight: 'bold',
//   margin: '10px 0 6px'
// };

// function CompanyTestSummary({ userdata }) {
//   const [loading, setLoading] = useState(true);
//   const [allPassedTests, setAllPassedTests] = useState([]);
//   const [companies, setCompanies] = useState([]); // [{ id: string, name: string }]
//   const [selectedCompany, setSelectedCompany] = useState('all'); // 'all' or companyID
//   const [isMobile, setIsMobile] = useState(
//     typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false
//   );

//   // Detect mobile
//   useEffect(() => {
//     if (typeof window === 'undefined') return;
//     const mql = window.matchMedia('(max-width: 640px)');
//     const handler = (e) => setIsMobile(e.matches);
//     mql.addEventListener('change', handler);
//     setIsMobile(mql.matches);
//     return () => mql.removeEventListener('change', handler);
//   }, []);

//   // Load list of companies
//   useEffect(() => {
//     if (!userdata) return;

//     const companiesRef = firebase.database.ref('active-companies');

//     companiesRef.once('value', (snap) => {
//       const comps = [];
//       snap.forEach((child) => {
//         const val = child.val();
//         if (val?.companyID && val?.companyName) {
//           comps.push({
//             id: val.companyID,
//             name: val.companyName,
//           });
//         }
//       });
//       comps.sort((a, b) => a.name.localeCompare(b.name));
//       setCompanies(comps);

//       // Default to current user's company if available
//       const userCompanyID = userdata.real_companyID || userdata.companyID;
//       if (userCompanyID && comps.some(c => c.id === userCompanyID)) {
//         setSelectedCompany(userCompanyID);
//       }
//     }).catch((err) => {
//       console.error('Error loading companies list:', err);
//     });
//   }, [userdata]);

//   // Load test results based on selection
//   useEffect(() => {
//     if (!userdata || companies.length === 0) return;

//     setLoading(true);
//     setAllPassedTests([]);

//     const processTest = (test, userId = null, companyId = null) => {
//       const total = parseInt(test.totalAnswered || test.qlen || 0);
//       const score = parseInt(test.testscore || 0);
//       const passedFlag =
//         typeof test.testPassed === 'boolean'
//           ? test.testPassed
//           : calculateTestPassed(score, total, test.difficulty || 'basic');

//       if (!passedFlag) return null;

//       const lc =
//         typeof test.lcEarned === 'number'
//           ? test.lcEarned
//           : passedFlag
//             ? calculateLCEarned(score, true, test.difficulty || 'basic')
//             : 0;

//       return {
//         surveyid: test.surveyid || '',
//         userId,
//         companyId,
//         category: test.leadCategory || test.categoryType || 'general',
//         subCategory: test.leadSubCategory || '',
//         lc,
//         difficulty: test.difficulty || 'basic',
//       };
//     };

//     const fetchForCompany = async (companyID) => {
//       const ref = firebase.database.ref(`userTestResults/${companyID}`);
//       try {
//         const snap = await ref.once('value');
//         const companyData = snap.val() || {};
//         const passed = [];

//         Object.entries(companyData).forEach(([userId, userTests]) => {
//           if (!userTests || typeof userTests !== 'object') return;
//           Object.entries(userTests).forEach(([surveyid, test]) => {
//             const processed = processTest({ ...test, surveyid }, userId, companyID);
//             if (processed) passed.push(processed);
//           });
//         });

//         return passed;
//       } catch (err) {
//         console.error(`Error reading company ${companyID}:`, err);
//         return [];
//       }
//     };

//     const loadData = async () => {
//       let passed = [];

//       if (selectedCompany === 'all') {
//         // Aggregate across all companies
//         for (const comp of companies) {
//           const companyPassed = await fetchForCompany(comp.id);
//           passed = passed.concat(companyPassed);
//         }
//       } else {
//         passed = await fetchForCompany(selectedCompany);
//       }

//       setAllPassedTests(passed);
//       setLoading(false);
//     };

//     loadData();

//     // No cleanup needed with .once()
//   }, [userdata, selectedCompany, companies]);

//   const totals = useMemo(() => {
//     const byCategory = {};
//     const bySubCategory = {};
//     let totalLC = 0;
//     let uniqueUsers = new Set();

//     allPassedTests.forEach((t) => {
//       const cat = t.category;
//       const sub = t.subCategory;
//       const lc = Number(t.lc || 0);

//       byCategory[cat] = (byCategory[cat] || 0) + lc;
//       if (sub) bySubCategory[sub] = (bySubCategory[sub] || 0) + lc;
//       totalLC += lc;
//       if (t.userId) uniqueUsers.add(t.userId);
//     });

//     return {
//       totalPassedTests: allPassedTests.length,
//       uniquePeoplePassed: uniqueUsers.size,
//       totalLC,
//       byCategory,
//       bySubCategory,
//     };
//   }, [allPassedTests]);

//   const cleanMainCategory = (name) => {
//     if (!name) return '—';
//     return String(name).split(/\s*[–—-]\s*/)[0].trim();
//   };

//   if (!userdata) {
//     return <div style={{ padding: 24 }}>Please sign in.</div>;
//   }

//   const companyName =
//     selectedCompany === 'all'
//       ? 'All Companies'
//       : companies.find(c => c.id === selectedCompany)?.name || 'Selected Company';

//   return (
//     <div style={{ padding: '16px 20px', maxWidth: 1100, margin: '0 auto' }}>
//       <h2 style={{ margin: '0 0 20px' }}>Test Results Overview – {companyName}</h2>

//       <div style={{ marginBottom: 24, maxWidth: 400 }}>
//         <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>
//           Select Company:
//         </label>
//         <select
//           value={selectedCompany}
//           onChange={(e) => setSelectedCompany(e.target.value)}
//           style={{
//             width: '100%',
//             padding: '10px',
//             fontSize: '16px',
//             borderRadius: '6px',
//             border: '1px solid #ccc',
//           }}
//         >
//           <option value="all">All Companies</option>
//           {companies.map((comp) => (
//             <option key={comp.id} value={comp.id}>
//               {comp.name}
//             </option>
//           ))}
//         </select>
//       </div>

//       {loading ? (
//         <div>Loading data...</div>
//       ) : (
//         <>
//           <div
//             style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
//               gap: 16,
//               marginBottom: 32,
//             }}
//           >
//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>
//                 People with ≥1 passed test
//               </div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.uniquePeoplePassed}</div>
//             </div>

//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>Total Passed Tests</div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalPassedTests}</div>
//             </div>

//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>Total LC Earned</div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalLC.toFixed(1)} LC</div>
//             </div>
//           </div>

//           <h3 style={{ margin: '32px 0 16px' }}>LC by Main Category</h3>
//           <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 32 }}>
//             {Object.keys(totals.byCategory).length === 0 ? (
//               <div style={{ color: '#94a3b8' }}>No passed tests recorded yet</div>
//             ) : (
//               Object.entries(totals.byCategory)
//                 .sort(([, a], [, b]) => b - a)
//                 .map(([cat, lc]) => (
//                   <div
//                     key={cat}
//                     style={{
//                       background: '#f1f5f9',
//                       borderRadius: 999,
//                       padding: '8px 16px',
//                       fontSize: 14,
//                     }}
//                   >
//                     <strong>{cleanMainCategory(cat)}</strong>: {lc.toFixed(1)} LC
//                   </div>
//                 ))
//             )}
//           </div>

//           {/* Optional: show sub-categories if needed */}
//           {/* <h3>LC by Sub-category</h3> ... similar block ... */}

//           {/* If you want to show the list of passed tests (anonymous): */}
//           {/* <h3>Passed Tests</h3>
//           {allPassedTests.length === 0 ? (
//             <p>No passed tests found.</p>
//           ) : (
//             <ul>
//               {allPassedTests.map(t => (
//                 <li key={t.surveyid + t.userId}>
//                   {t.category} → {t.subCategory || '—'} • {t.lc.toFixed(1)} LC
//                 </li>
//               ))}
//             </ul>
//           )} */}
//         </>
//       )}
//     </div>
//   );
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null,
// });

// export default connect(mapStateToProps)(CompanyTestSummary);

//////////////////////////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useState } from 'react'
// import { connect } from 'react-redux'
// import firebase from '../store/firebase'
// import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils'

// function CompanyTestSummary({ userdata }) {
//   const [loading, setLoading] = useState(true)
//   const [allPassedTests, setAllPassedTests] = useState([]) // flattened list of only passed tests

//   useEffect(() => {
//     console.log('CompanyTestSummary userdata: ', userdata);

//     if (!userdata) return

//     const companyID = '-NRbTwxbqFLepk62Eq6M';//userdata.real_companyID || userdata.companyID

//     console.log('CompanyTestSummary companyID: ', companyID);

//     if (!companyID) return
//  console.log('CompanyTestSummary 1: ', companyID);

// // const ref = firebase.database.ref(`userTestResults/${companyID}`)
// const ref = firebase.database.ref(`userTestResults/${companyID}`);
// console.log('CompanyTestSummary ref path: ', ref.toString());  // should print full https://....firebaseio.com/userTestResults/-NRb... path

// ref.once('value')
//   .then(snap => {
//     console.log('CompanyTestSummary .once test → exists:', snap.exists(), 'value type:', typeof snap.val());
//   })
//   .catch(err => {
//     console.error('CompanyTestSummary READ ERROR:', err.code, err.message);
//   });
//  console.log('CompanyTestSummary 2: ', companyID);

//     const handle = ref.on('value', (snap) => {
//       const companyData = snap.val() || {}

//       const passed = []
// console.log('CompanyTestSummary 3: ', companyData);

//       Object.entries(companyData).forEach(([userId, userTests]) => {
//         if (!userTests || typeof userTests !== 'object') return

//         Object.entries(userTests).forEach(([surveyid, test]) => {
//           const total = parseInt(test.totalAnswered || test.qlen || 0)
//           const score = parseInt(test.testscore || 0)
//           const passedFlag =
//             typeof test.testPassed === 'boolean'
//               ? test.testPassed
//               : calculateTestPassed(score, total, test.difficulty || 'basic')

//           if (!passedFlag) return

//           const lc =
//             typeof test.lcEarned === 'number'
//               ? test.lcEarned
//               : passedFlag
//                 ? calculateLCEarned(score, true, test.difficulty || 'basic')
//                 : 0

//           passed.push({
//             surveyid,
//             userId,           // optional – remove if you really want zero personal info
//             category: test.leadCategory || test.categoryType || 'general',
//             subCategory: test.leadSubCategory || '',
//             lc,
//             difficulty: test.difficulty || 'basic',
//             // ... add timestamp, title etc only if needed later
//           })
//         })
//       })

//       setAllPassedTests(passed)
//       setLoading(false)
//     })

//     return () => ref.off('value', handle)
//   }, [userdata])

//   const totals = useMemo(() => {
//     const byCategory = {}
//     const bySubCategory = {}
//     let totalLC = 0
//     let uniqueUsers = new Set()

//     allPassedTests.forEach((t) => {
//       const cat = t.category
//       const sub = t.subCategory
//       const lc = Number(t.lc || 0)

//       byCategory[cat] = (byCategory[cat] || 0) + lc
//       if (sub) bySubCategory[sub] = (bySubCategory[sub] || 0) + lc
//       totalLC += lc
//       uniqueUsers.add(t.userId)
//     })

//     return {
//       totalPassedTests: allPassedTests.length,
//       uniquePeoplePassed: uniqueUsers.size,
//       totalLC,
//       byCategory,
//       bySubCategory,
//     }
//   }, [allPassedTests])

//   const cleanMainCategory = (name) => {
//     if (!name) return '—'
//     return String(name).split(/\s*[–—-]\s*/)[0].trim()
//   }

//   if (!userdata) {
//     return <div style={{ padding: 24 }}>Please sign in.</div>
//   }

//   return (
//     <div style={{ padding: '16px 20px', maxWidth: 900, margin: '0 auto' }}>
//       <h2 style={{ margin: '0 0 24px' }}>Company Test Results Overview</h2>

//       {loading ? (
//         <div>Loading company data...</div>
//       ) : (
//         <>
//           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>People who passed ≥1 test</div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.uniquePeoplePassed}</div>
//             </div>

//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>Total passed tests</div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalPassedTests}</div>
//             </div>

//             <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
//               <div style={{ color: '#64748b', fontSize: 14, marginBottom: 4 }}>Total LC earned</div>
//               <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totals.totalLC.toFixed(1)} LC</div>
//             </div>
//           </div>

//           <h3 style={{ margin: '32px 0 16px' }}>LC by Main Category</h3>
//           <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
//             {Object.keys(totals.byCategory).length === 0 ? (
//               <div style={{ color: '#94a3b8' }}>No passed tests recorded yet</div>
//             ) : (
//               Object.entries(totals.byCategory)
//                 .sort(([,a], [,b]) => b - a)
//                 .map(([cat, lc]) => (
//                   <div
//                     key={cat}
//                     style={{
//                       background: '#f1f5f9',
//                       borderRadius: 999,
//                       padding: '8px 16px',
//                       fontSize: 14,
//                     }}
//                   >
//                     <strong>{cleanMainCategory(cat)}</strong>: {lc.toFixed(1)} LC
//                   </div>
//                 ))
//             )}
//           </div>

//           {/* Optional: add sub-category block if needed */}
//         </>
//       )}
//     </div>
//   )
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null
// })

// export default connect(mapStateToProps)(CompanyTestSummary)